import { useCallback, useEffect, useState } from "react";

/**
 * Shared PWA install hook. Listens once for `beforeinstallprompt` and exposes:
 * - canInstall: native install prompt is queued and ready
 * - install():   trigger the OS install dialog
 * - isStandalone: app is already running in installed/standalone mode
 * - isIos:       Safari iOS — needs custom "Add to Home Screen" hint
 *
 * Multiple components (header button + bottom banner) can use this hook in parallel.
 * The deferred event is stored on `window.__sw_install_event` so it isn't lost
 * when only one listener captures it.
 */
function detectIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
}
function detectStandalone() {
  if (typeof window === "undefined") return false;
  // TWA (Trusted Web Activity) — installed Android app via Bubblewrap sets
  // referrer to `android-app://<package>`. This is the most reliable TWA signal
  // and covers cases where display-mode isn't set to standalone.
  const isTWA = typeof document !== "undefined"
    && typeof document.referrer === "string"
    && document.referrer.startsWith("android-app://");
  return (
    isTWA ||
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function usePwaInstall() {
  const [deferred, setDeferred] = useState(() => (typeof window !== "undefined" ? window.__sw_install_event || null : null));
  const [installed, setInstalled] = useState(() => detectStandalone());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      window.__sw_install_event = e;
      setDeferred(e);
    };
    const onInstalled = () => {
      window.__sw_install_event = null;
      setDeferred(null);
      setInstalled(true);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    try {
      const res = await deferred.userChoice;
      if (res?.outcome === "accepted") {
        window.__sw_install_event = null;
        setDeferred(null);
      }
      return res;
    } catch (e) {
      return { outcome: "dismissed" };
    }
  }, [deferred]);

  return {
    canInstall: !!deferred && !installed,
    isStandalone: installed,
    isIos: detectIos(),
    install,
  };
}
